import React from "react";
import {
  Box,
  Drawer,
  Fab,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  Typography,
} from "@mui/material";
import AccessibleIcon from "@mui/icons-material/Accessible";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import TextIncreaseIcon from "@mui/icons-material/TextIncrease";
import ContrastIcon from "@mui/icons-material/Contrast";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import LinkIcon from "@mui/icons-material/Link";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

export default function AccessibilityMenu() {
  const [open, setOpen] = React.useState(false);
  const [showAbout, setShowAbout] = React.useState(false);
  const [showAboutMore, setShowAboutMore] = React.useState(false);
  const [settings, setSettings] = React.useState({});

  const SETTINGS_KEY = 'pych_accessibility';

  const toggle = (val) => () => setOpen(val);

  const applySettings = (s = {}) => {
    try {
      const targets = [document.documentElement, document.body]; // html and body
      // remove all possible accessibility classes first
      targets.forEach((t) =>
        t.classList.remove(
          'acc-large-text',
          'acc-zoom',
          'acc-readable-font',
          'acc-light-bg',
          'acc-dark-bg',
          'acc-high-contrast',
          'acc-highlight-links'
        )
      );

      if (s.largeText) targets.forEach((t) => t.classList.add('acc-large-text'));
      if (s.zoom) targets.forEach((t) => t.classList.add('acc-zoom'));
      if (s.readableFont) targets.forEach((t) => t.classList.add('acc-readable-font'));
      if (s.lightBg) targets.forEach((t) => t.classList.add('acc-light-bg'));
      if (s.darkBg) targets.forEach((t) => t.classList.add('acc-dark-bg'));
      if (s.highContrast) targets.forEach((t) => t.classList.add('acc-high-contrast'));
      if (s.highlightLinks) targets.forEach((t) => t.classList.add('acc-highlight-links'));

      // mark a data attribute so we can target MUI components and other libraries
      if (s.darkBg) {
        document.documentElement.setAttribute('data-acc-theme', 'dark');
        document.body.setAttribute('data-acc-theme', 'dark');
      } else if (s.highContrast) {
        document.documentElement.setAttribute('data-acc-theme', 'high-contrast');
        document.body.setAttribute('data-acc-theme', 'high-contrast');
      } else if (s.lightBg) {
        document.documentElement.setAttribute('data-acc-theme', 'light');
        document.body.setAttribute('data-acc-theme', 'light');
      } else {
        document.documentElement.removeAttribute('data-acc-theme');
        document.body.removeAttribute('data-acc-theme');
      }
    } catch (e) {
      // ignore for server-side render or errors
      console.error(e);
    }
  };

  React.useEffect(() => {
    // restore saved settings
    try {
      const saved = JSON.parse(localStorage.getItem(SETTINGS_KEY) || 'null');
      if (saved) {
        setSettings(saved);
        applySettings(saved);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const persist = (s) => {
    setSettings(s);
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
    } catch (e) {}
    applySettings(s);
  };

  const disableAll = () => {
    const s = {
      largeText: false,
      zoom: false,
      readableFont: false,
      lightBg: false,
      darkBg: false,
      highContrast: false,
      highlightLinks: false,
    };
    try {
      localStorage.removeItem(SETTINGS_KEY);
    } catch (e) {}
    persist(s);
  };

  const showAboutFunc = () => {
    setShowAbout(!showAbout);
  };

  const aboutFunc = () => {
    setShowAboutMore(!showAboutMore);
  };


  const toggleSetting = (key) => {
    const next = { ...settings };
    // special handling for bg modes (exclusive)
    if (key === 'lightBg') {
      next.lightBg = !next.lightBg;
      if (next.lightBg) next.darkBg = false;
    } else if (key === 'darkBg') {
      next.darkBg = !next.darkBg;
      if (next.darkBg) next.lightBg = false;
    } else {
      next[key] = !next[key];
    }
    persist(next);
  };

  const act = (name) => () => {
    switch (name) {
      case 'increase-text':
        toggleSetting('largeText');
        break;
      case 'zoom':
        toggleSetting('zoom');
        break;
      case 'readable-font':
        toggleSetting('readableFont');
        break;
      case 'light-bg':
        toggleSetting('lightBg');
        break;
      case 'dark-bg':
        toggleSetting('darkBg');
        break;
      case 'high-contrast':
        toggleSetting('highContrast');
        break;
      case 'highlight-links':
        toggleSetting('highlightLinks');
        break;
      case 'show-about':
        showAboutFunc();
        break;
      case 'about':
        aboutFunc();
        break;
      case 'reset':
        disableAll();
        break;
      case 'read':
        startReading();
        break;
      case 'pause-read':
        pauseReading();
        break;
      case 'resume-read':
        resumeReading();
        break;
      case 'stop-read':
        stopReading();
        break;
      default:
        console.log('unknown action', name);
    }
  };

  // Text-to-speech (Web Speech API) - improved for Hebrew
  const [readingState, setReadingState] = React.useState('stopped'); // 'stopped' | 'reading' | 'paused'
  const utteranceQueueRef = React.useRef([]);
  const utteranceIndexRef = React.useRef(0);

  const splitIntoChunks = (text, maxLen = 8000) => {
    const words = text.split(/\s+/);
    const chunks = [];
    let cur = '';
    for (let w of words) {
      if ((cur + ' ' + w).length > maxLen) {
        if (cur) chunks.push(cur.trim());
        cur = w;
      } else {
        cur = cur ? cur + ' ' + w : w;
      }
    }
    if (cur) chunks.push(cur.trim());
    return chunks;
  };

  const findHebrew = (text) => /[\u0590-\u05FF]/.test(text);

  const chooseVoiceForLang = (langPrefix) => {
    const voices = window.speechSynthesis.getVoices() || [];
    // first try exact lang match
    let v = voices.find((vv) => vv.lang && vv.lang.toLowerCase().startsWith(langPrefix));
    if (v) return v;
    // then try name heuristics
    v = voices.find((vv) => /hebrew|ivrit|he-/i.test(vv.name || ''));
    if (v) return v;
    return voices[0] || null;
  };

  const speakNext = () => {
    const idx = utteranceIndexRef.current;
    const queue = utteranceQueueRef.current || [];
    if (!queue || idx >= queue.length) {
      setReadingState('stopped');
      utteranceQueueRef.current = [];
      utteranceIndexRef.current = 0;
      return;
    }
    const u = queue[idx];
    u.onend = () => {
      // advance
      utteranceIndexRef.current += 1;
      // small timeout to allow pause/cancel logic
      setTimeout(() => {
        if (readingState === 'reading') speakNext();
      }, 50);
    };
    u.onerror = () => {
      setReadingState('stopped');
      utteranceQueueRef.current = [];
      utteranceIndexRef.current = 0;
    };
    window.speechSynthesis.speak(u);
    setReadingState('reading');
  };

  const startReading = () => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      window.alert('Text-to-speech אינו נתמך בדפדפן זה.');
      return;
    }
    stopReading();
    const rootEl = document.querySelector('#root') || document.body;
    let text = '';
    try {
      text = rootEl.innerText || rootEl.textContent || '';
    } catch (e) {
      text = document.body.innerText || document.body.textContent || '';
    }
    text = (text || '').replace(/\s+/g, ' ').trim();
    if (!text) {
      window.alert('לא נמצא טקסט לקריאה.');
      return;
    }
    // prefer Hebrew if page contains Hebrew characters
    const isHeb = findHebrew(text);
    const lang = isHeb ? 'he' : (document.documentElement.lang || navigator.language || 'en');

    // prepare chunks
    const chunks = splitIntoChunks(text, 7000);
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = isHeb ? chooseVoiceForLang('he') : chooseVoiceForLang((lang || '').split('-')[0]);

    const utterances = chunks.map((chunk) => {
      const u = new SpeechSynthesisUtterance(chunk);
      try {
        if (isHeb) u.lang = 'he-IL';
        else u.lang = lang;
      } catch (e) {}
      if (preferredVoice) {
        try { u.voice = preferredVoice; } catch (e) {}
      }
      u.rate = 1;
      u.pitch = 1;
      return u;
    });

    utteranceQueueRef.current = utterances;
    utteranceIndexRef.current = 0;
    // if voices not yet loaded, wait for them then set voices on utterances
    if (!voices || voices.length === 0) {
      // onvoiceschanged may fire
      const onVoicesChanged = () => {
        const v = isHeb ? chooseVoiceForLang('he') : chooseVoiceForLang((lang || '').split('-')[0]);
        if (v) utteranceQueueRef.current.forEach((u) => { try { u.voice = v; } catch (e) {} });
        window.speechSynthesis.removeEventListener('voiceschanged', onVoicesChanged);
        speakNext();
      };
      window.speechSynthesis.addEventListener('voiceschanged', onVoicesChanged);
      // kick off if event doesn't fire
      setTimeout(() => { if (window.speechSynthesis.getVoices().length) speakNext(); }, 200);
    } else {
      speakNext();
    }
  };

  const pauseReading = () => {
    if (window.speechSynthesis && window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();
      setReadingState('paused');
    }
  };

  const resumeReading = () => {
    if (window.speechSynthesis && window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setReadingState('reading');
    }
  };

  const stopReading = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    utteranceQueueRef.current = [];
    utteranceIndexRef.current = 0;
    setReadingState('stopped');
  };

  return (
    <>
      <Fab
        aria-label="פתח תפריט נגישות"
        onClick={() => setOpen(!open)}
        sx={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: 9999,
          width: 72,
          height: 72,
          boxShadow: 6,
        }}
      >
        <AccessibleIcon sx={{ fontSize: 40 }} />
      </Fab>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        keepMounted
        PaperProps={{
          sx: { width: 360, direction: "rtl", p: 1 },
        }}
      >
        <Box role="presentation">
          <Typography
            variant="h6"
            sx={{ px: 2, pt: 1, pb: 1, fontWeight: 700 }}
          >
            נגישות טקסט
          </Typography>

          <List subheader={<li />}>
            <ListItemButton onClick={act("increase-text")}>
              <ListItemIcon>
                <ZoomInIcon />
              </ListItemIcon>
              <ListItemText primary="לחץ להגדלת הטקסט בעזרת זכוכית מגדלת" />
            </ListItemButton>
            <ListItemButton onClick={act("zoom")}>
              <ListItemIcon>
                <TextIncreaseIcon />
              </ListItemIcon>
              <ListItemText primary="לחץ להגדלת הטקסט" />
            </ListItemButton>
            <ListItemButton onClick={act("readable-font")}>
              <ListItemIcon>
                <AccessibilityNewIcon />
              </ListItemIcon>
              <ListItemText primary="שנה לפונט קריא יותר" />
            </ListItemButton>
          </List>

          <Divider sx={{ my: 1 }} />

          <List
            subheader={
              <ListSubheader component="div" sx={{ lineHeight: 2, fontSize: 18 }}>
                קרא את האתר
              </ListSubheader>
            }
          >
            {/* simple Hebrew labels for state */}
            <ListItemButton onClick={act('read')}>
              <ListItemIcon>
                <VolumeUpIcon />
              </ListItemIcon>
              <ListItemText primary="התחל לקרוא את האתר" secondary={readingState === 'reading' ? 'קורא...' : readingState === 'paused' ? 'מושהה' : 'נעצר'} />
            </ListItemButton>

            <ListItemButton onClick={act('pause-read')} disabled={readingState !== 'reading'}>
              <ListItemIcon>
                <PauseIcon />
              </ListItemIcon>
              <ListItemText primary="השהה קריאה" />
            </ListItemButton>

            <ListItemButton onClick={act('resume-read')} disabled={readingState !== 'paused'}>
              <ListItemIcon>
                <PlayArrowIcon />
              </ListItemIcon>
              <ListItemText primary="המשך קריאה" />
            </ListItemButton>

            <ListItemButton onClick={act('stop-read')} disabled={readingState === 'stopped'}>
              <ListItemIcon>
                <RestartAltIcon />
              </ListItemIcon>
              <ListItemText primary="עצור קריאה" />
            </ListItemButton>
          </List>


          <List
            subheader={
              <ListSubheader component="div" sx={{ lineHeight: 2, fontSize: 18 }}>
                נגישויות צבעוניות
              </ListSubheader>
            }
          >
            <ListItemButton onClick={act("light-bg")}>
              <ListItemIcon>
                <Brightness4Icon />
              </ListItemIcon>
              <ListItemText primary="שינוי הרקע לבהיר" />
            </ListItemButton>
            <ListItemButton onClick={act("dark-bg")}>
              <ListItemIcon>
                <Brightness4Icon />
              </ListItemIcon>
              <ListItemText primary="שינוי הרקע לכהה" />
            </ListItemButton>
            <ListItemButton onClick={act("high-contrast")}>
              <ListItemIcon>
                <ContrastIcon />
              </ListItemIcon>
              <ListItemText primary="חזית צבעי בררת המחדל" />
            </ListItemButton>
          </List>

          <Divider sx={{ my: 1 }} />

          <List
            subheader={
              <ListSubheader component="div" sx={{ lineHeight: 2, fontSize: 18 }}>
                נגישות לקישורים
              </ListSubheader>
            }
          >
            <ListItemButton onClick={act("highlight-links")}>
              <ListItemIcon>
                <LinkIcon />
              </ListItemIcon>
              <ListItemText primary="הדגשת קישורים" />
            </ListItemButton>
          </List>

          <Divider sx={{ my: 1 }} />

          <List
            subheader={
              <ListSubheader component="div" sx={{ lineHeight: 2, fontSize: 18 }}>
                עוד
              </ListSubheader>
            }
          >
            <ListItemButton onClick={act("show-about")}>
              <ListItemIcon>
                <AccessibleIcon />
              </ListItemIcon>
              <ListItemText primary="הצהרת נגישות" />
            </ListItemButton>
            {showAbout && (
                <div>
                    <h6>הצהרת נגישות</h6>
                    <div>באתר זה מותקן רכיב הנגשה המאפשר לבעלי מוגבלויות לגלוש באתר בצורה תקינה באמצעות תוכנות עזר, בהתאם לתקן AA, ובהתאם לחוק הנגישות.</div>
                    <p>שימוש בתוכנות עזר</p>
                    <ul>
                        <li>מבנה האתר בוצע כך ששימוש בתוכנת עזר לבעלי מוגבלויות מאפשר לקרוא את כל התוכן באתר באופן מלא ופשוט.</li>
                        <li>ההנגשה המבוצעת באתר כוללת תמיכה בתוכנות ושינוי צבעוניות אתר.</li>
                        <li>הפעלת מנגנון הנגישות מתאפשר רק לאחר טעינה מלאה של הדף.</li>
                    </ul>
                    <h6>עזרה טכנית</h6>
                    <div>במידה והנכם מגלים תוך כדי גלישה אזורים לא מונגשים, נודה לכם באם תשלחו לנו הודעה על כך בטופס יצירת קשר של האתר עם קישור לדף.</div>
                </div>
            )}
            

            <ListItemButton onClick={act("about")}>
              <ListItemIcon>
                <InfoOutlinedIcon />
              </ListItemIcon>
              <ListItemText primary="הסבר אודות תפריט נגישות" />
            </ListItemButton>
            {showAboutMore && (
                <div>
                    <h6>הסבר על אופן השימוש</h6>
                    <ul>
                        <li>תוסף הנגישות הקיים באתר עובד עם דפדפנים עיקריים בשוק, כגון: פיירפוקס, כרום, אקספלורר.</li>
                        <li>ההנגשה המבוצעת באתר כוללת תמיכה בתוכנות  ושינוי צבעוניות אתר.</li>
                        <li>הפעלת מנגנון הנגישות מתאפשר רק לאחר טעינה מלאה של הדף.</li>
                    </ul>
                    <h6>כיצד משתמשים בתפריט ניווט הנגישות?</h6>
                    <ul>
                        <li>כדי להפעיל את מנגנון הנגישות יש ללחוץ על האייקון בחלק העליון של האתר או לחיצה על מקש ה Tab+Enter.</li>
                        <li>לחיצה נוספת על מקש Tab מאפשרת לעבור בין כל התפריטים באתר.</li>
                        <li>לחיצה נוספת על האייקון סוגרת את מנגנון הנגישות.</li>
                        <li>בכניסה ראשונה לאתר ניתן לבחור את סגנון התצוגה הרצוי, ולאחר מכן בחירה זו תלווה את הגולש בכל דפי האתר.</li>
                    </ul>
                    <h6>עזרה טכנית</h6>
                    <div>אנו פועלים להטמעת כל הנחיות הנגישות על פי המתואר בהנחיות הנגישות 2.0 WCAG. במידה והנכם מגלים תוך כדי גלישה אזורים לא מונגשים, נודה לכם באם תשלחו לנו הודעה על כך בטופס יצירת קשר של האתר עם קישור לדף ותיאור מלא של מערכת ההפעלה בה הנכם משתמשים, סוג דפדפן, וגרסת דפדפן.</div>
                   
                </div>
            )}
            

            <ListItemButton onClick={act("reset")}>
              <ListItemIcon>
                <RestartAltIcon />
              </ListItemIcon>
              <ListItemText primary="איפוס להגדרות נגישות" />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
    </>
  );
}
